/**
 * @file     MlsDisclaimer.tsx
 * @readme   ../CLAUDE.md
 * @purpose  MLS® Reciprocity disclaimer and consumer-use notice required by §8.12.05 and §8.12.07(b).
 */
"use client";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { useAppTheme } from "@/core/theme/useAppTheme";
import { LICENSEE_LINE } from "@/core/config/brand/licenseeLine";
import { MLS_ALL_FEEDS_FOOTNOTE, MLS_RECIPROCITY_NOTICE } from "@/core/config/mlsNotices";

interface Props {
  /**
   * `full` (default) renders both the reciprocity notice and the
   * consumer-use notice with a top border — the §8 standard placement.
   * `compact` renders the board and CREA DDF® notices in one line for
   * above-the-fold placement (e.g. just above a listing grid) so users who
   * never scroll past page 1 still see them. Grids can mix both feeds.
   */
  variant?: "full" | "compact";
}

export function MlsDisclaimer({ variant = "full" }: Props) {
  const { theme } = useAppTheme();
  const { t } = useTranslation();

  if (variant === "compact") {
    return (
      <Typography
        sx={{
          fontSize: 11,
          lineHeight: 1.5,
          color: theme.semanticColors.textMuted,
        }}
      >
        {t("properties.disclaimers.reciprocityNoticeCompact", {
          defaultValue: MLS_ALL_FEEDS_FOOTNOTE,
        })}
      </Typography>
    );
  }

  return (
    <Box
      component="footer"
      sx={{
        mt: 4,
        pt: 3,
        pb: 2,
        borderTop: `1px solid ${theme.semanticColors.borderSubtle}`,
      }}
    >
      {/* TODO: Add MLS® Reciprocity Logo once obtained from GVR Member portal
      <Image src="/assets/compliance/mls-reciprocity-logo.svg" alt="MLS® Reciprocity" width={95} height={35} />
      */}
      <Typography
        sx={{
          fontSize: 11,
          lineHeight: 1.5,
          color: theme.semanticColors.textMuted,
          maxWidth: 800,
        }}
      >
        {t("properties.disclaimers.reciprocityNotice", {
          defaultValue: MLS_RECIPROCITY_NOTICE,
        })}
      </Typography>
      <Typography
        sx={{
          fontSize: 11,
          lineHeight: 1.5,
          color: theme.semanticColors.textMuted,
          mt: 1,
          maxWidth: 800,
        }}
      >
        {t("properties.disclaimers.consumerUseNotice", {
          defaultValue:
            "This website must only be used by consumers for the purpose of locating and purchasing real estate.",
        })}
      </Typography>
      <Typography
        sx={{
          fontSize: 11,
          lineHeight: 1.5,
          color: theme.semanticColors.textMuted,
          mt: 1,
          maxWidth: 800,
        }}
      >
        {t("footer.trust.credit", { defaultValue: LICENSEE_LINE })}
      </Typography>
    </Box>
  );
}
